import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import dayjs from 'dayjs';

import { ApplicationConfigService } from '../config/application-config.service';
import { createRequestOption } from '../request/request-util';
import { isPresent } from '../util/operators';
import { DATE_FORMAT } from '../util/input.constants';
import { getDadosUsuarioIdentifier, IDadosUsuario } from '../models/dados-usuario.model';
import { ICompraMultiDTO } from '../models/compraMultiDTO.model';

export type EntityResponseType = HttpResponse<IDadosUsuario>;
export type EntityArrayResponseType = HttpResponse<IDadosUsuario[]>;

export type EntityResponseTypeMultiDTO = HttpResponse<ICompraMultiDTO>;




@Injectable({ providedIn: 'root' })
export class DadosUsuarioService {
  protected resourceUrl = this.applicationConfigService.getEndpointFor('api/dados-usuarios');

  constructor(protected http: HttpClient, protected applicationConfigService: ApplicationConfigService) {}

  create(dadosUsuario: IDadosUsuario): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(dadosUsuario);
    return this.http
      .post<IDadosUsuario>(this.resourceUrl, copy, { observe: 'response' })
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  update(dadosUsuario: IDadosUsuario): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(dadosUsuario);
    return this.http
      .put<IDadosUsuario>(`${this.resourceUrl}/${getDadosUsuarioIdentifier(dadosUsuario) as number}`, copy, { observe: 'response' })
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  partialUpdate(dadosUsuario: IDadosUsuario): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(dadosUsuario);
    return this.http
      .patch<IDadosUsuario>(`${this.resourceUrl}/${getDadosUsuarioIdentifier(dadosUsuario) as number}`, copy, { observe: 'response' })
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  find(id: number): Observable<EntityResponseType> {
    return this.http
      .get<IDadosUsuario>(`${this.resourceUrl}/${id}`, { observe: 'response' })
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  query(req?: any): Observable<EntityArrayResponseType> {

    return this.http
      .get<IDadosUsuario[]>(`${this.resourceUrl}/list`, { observe: 'response' })
      .pipe(map((res: EntityArrayResponseType) => this.convertDateArrayFromServer(res)));
  }

  lista(req?: any): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.http
      .get<IDadosUsuario[]>(this.resourceUrl, { params: options, observe: 'response' })
      .pipe(map((res: EntityArrayResponseType) => this.convertDateArrayFromServer(res)));
  }

  findByCreditDisponivel(req?: any, total?:any): Observable<EntityResponseTypeMultiDTO> {
    const options = createRequestOption(req);
    return this.http
      .get<ICompraMultiDTO>(this.resourceUrl+"/find-fila-creditos", { params: {qntUnitaria: req, total:total}, observe: 'response' })
      .pipe(map((res: EntityResponseTypeMultiDTO) => (res)));
  }

  findByCreditDisponivelCooperativa(qntUnitaria?: any, total?:any, cooperativaId?:any): Observable<EntityResponseTypeMultiDTO> {
    console.log({qntUnitaria: qntUnitaria, total:total, cooperativaId:cooperativaId})
    return this.http
      .post<ICompraMultiDTO>(`${this.resourceUrl}/find-fila-creditos-cooperativa`, {qntUnitaria: qntUnitaria, total:total, cooperativaId:cooperativaId}, { observe: 'response' })
      .pipe(map((res: EntityResponseTypeMultiDTO) => (res)));
    
  }

  produtorCount(): Observable<HttpResponse<number>>{
    return this.http.get<number>(`${this.resourceUrl}/produtor-count`, {observe: 'response' });
  }

  produtoresEstoque(): Observable<HttpResponse<number>>{
    return this.http.get<number>(`${this.resourceUrl}/estoque`, {observe: 'response' });
  }

  findAllPageable(req?: any): Observable<any> {
    return this.http
      .get<any[]>(`${this.resourceUrl}/pageable`, { params: req, observe: 'response' })
      .pipe(map((res: any) => this.convertPageFromServer(res)));
  }

  findByCooperativa(req?: any): Observable<any> {
    return this.http
      .get<any[]>(`${this.resourceUrl}/find-by-cooperativa`, { params: req, observe: 'response' })
      .pipe(map((res: any) => this.convertPageFromServer(res)));
  }

  delete(id: number): Observable<HttpResponse<{}>> {
    return this.http.delete(`${this.resourceUrl}/${id}`, { observe: 'response' });
  }

  disableUser(dadosUsuario: IDadosUsuario): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(dadosUsuario);
    return this.http
      .put<IDadosUsuario>(`${this.resourceUrl}/disable`, copy, { observe: 'response' })
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  addDadosUsuarioToCollectionIfMissing(
    dadosUsuarioCollection: IDadosUsuario[],
    ...dadosUsuariosToCheck: (IDadosUsuario | null | undefined)[]
  ): IDadosUsuario[] {
    const dadosUsuarios: IDadosUsuario[] = dadosUsuariosToCheck.filter(isPresent);
    if (dadosUsuarios.length > 0) {
      const dadosUsuarioCollectionIdentifiers = dadosUsuarioCollection.map(
        dadosUsuarioItem => getDadosUsuarioIdentifier(dadosUsuarioItem)!
      );
      const dadosUsuariosToAdd = dadosUsuarios.filter(dadosUsuarioItem => {
        const dadosUsuarioIdentifier = getDadosUsuarioIdentifier(dadosUsuarioItem);
        if (dadosUsuarioIdentifier == null || dadosUsuarioCollectionIdentifiers.includes(dadosUsuarioIdentifier)) {
          return false;
        }
        dadosUsuarioCollectionIdentifiers.push(dadosUsuarioIdentifier);
        return true;
      });
      return [...dadosUsuariosToAdd, ...dadosUsuarioCollection];
    }
    return dadosUsuarioCollection;
  }

  protected convertDateFromClient(dadosUsuario: IDadosUsuario): IDadosUsuario {
    return Object.assign({}, dadosUsuario, {
      dataNascimento: dadosUsuario.dataNascimento?.isValid() ? dadosUsuario.dataNascimento.format(DATE_FORMAT) : undefined,
    });
  }

  protected convertDateFromServer(res: EntityResponseType): EntityResponseType {
    if (res.body) {
      res.body.dataNascimento = res.body.dataNascimento ? dayjs(res.body.dataNascimento) : undefined;
    }
    return res;
  }

  protected convertDateArrayFromServer(res: EntityArrayResponseType): EntityArrayResponseType {
    if (res.body) {
      res.body.forEach((dadosUsuario: IDadosUsuario) => {
        dadosUsuario.dataNascimento = dadosUsuario.dataNascimento ? dayjs(dadosUsuario.dataNascimento) : undefined;
      });
    }
    return res;
  }

  protected convertPageFromServer(res: any): any {
    if (res.body) {
      res.body.content.forEach((dadosUsuario: IDadosUsuario) => {
        dadosUsuario.dataNascimento = dadosUsuario.dataNascimento ? dayjs(dadosUsuario.dataNascimento) : undefined;
      });
    }
    return res;
  }

  notificaSaldoDisponivelSaque(): Observable<HttpResponse<{}>> {
    return this.http.get<any>(`${this.resourceUrl}/notifica-balance-disponivel`,  { observe: 'response' })      
  }
}
